import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { trpc } from "../../lib/api/trpc";
import { usePermissions } from "../../hooks/usePermissions";

type UsageSummary = {
  plan: {
    name: string;
    displayName: string;
    monthlyPriceCents: number;
    features: Record<string, unknown>;
  };
  orgStatus: string;
  seats: {
    used: number;
    max: number;
    remaining: number | null;
    percentUsed: number;
  };
  documents: {
    current: number;
    max: number;
    remaining: number | null;
    percentUsed: number;
  };
  apiCalls: {
    usedThisMonth: number;
    used: number;
    max: number;
    remaining: number | null;
    percentUsed: number;
  };
  storage: { currentBytes: number; currentMB: number };
};

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    TRIAL: "bg-warn-bg text-warn",
    ACTIVE: "bg-ok-bg text-ok",
    SUSPENDED: "bg-err-bg text-err",
    ARCHIVED: "bg-surface-2 text-text-faint",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.ACTIVE}`}
    >
      {status}
    </span>
  );
}

function UsageMeter({
  label,
  used,
  max,
  remaining,
  percentUsed,
  subtitle,
  link,
  linkLabel,
}: {
  label: string;
  used: number;
  max: number;
  remaining: number | null;
  percentUsed: number;
  subtitle?: string;
  link?: string;
  linkLabel?: string;
}) {
  const isUnlimited = max === 0;
  const barColor =
    percentUsed > 90
      ? "bg-red-500"
      : percentUsed > 75
        ? "bg-amber-500"
        : "bg-green-500";

  return (
    <div className="bg-surface rounded-lg shadow-box p-6">
      <h3 className="text-sm font-semibold text-text-base mb-3">{label}</h3>
      <div className="text-2xl font-bold text-text-base mb-1">
        {used}
        {!isUnlimited && (
          <span className="text-sm font-normal text-text-faint"> / {max}</span>
        )}
      </div>

      {isUnlimited ? (
        <p className="text-sm text-text-faint">Unlimited</p>
      ) : (
        <>
          <div className="w-full bg-surface-2 rounded-full h-2 mb-2">
            <div
              className={`h-2 rounded-full ${barColor} transition-all`}
              style={{ width: `${Math.min(percentUsed, 100)}%` }}
            />
          </div>
          <p className="text-xs text-text-faint">
            {remaining !== null ? `${remaining} remaining` : "Unlimited"}
          </p>
        </>
      )}

      {subtitle && <p className="text-xs text-text-faint mt-1">{subtitle}</p>}

      {link && linkLabel && (
        <Link
          to={link}
          className="text-xs text-accent hover:underline mt-2 inline-block"
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

export function OrganisationPage() {
  const { isAdmin } = usePermissions();
  const [editingName, setEditingName] = useState(false);
  const [orgName, setOrgName] = useState("");
  const utils = trpc.useContext();

  // @ts-ignore
  const { data: orgData } = trpc.organization.getCurrent.useQuery();
  const org = orgData as {
    id: string;
    name: string;
    slug: string;
    createdAt: string;
  } | null;

  // @ts-ignore
  const { data, isLoading } = trpc.organization.getUsageSummary.useQuery();
  const summary = data as UsageSummary | undefined;

  const handleStartEdit = () => {
    if (org) {
      setOrgName(org.name);
      setEditingName(true);
    }
  };

  const handleSaveName = async () => {
    if (!orgName.trim()) return;
    try {
      // @ts-ignore
      await utils.client.organization.update.mutate({ name: orgName.trim() });
      // @ts-ignore
      await utils.organization.getCurrent.invalidate();
      setEditingName(false);
    } catch {
      // silent
    }
  };

  // API call reset date — always the 1st of next month from today
  const today = new Date();
  const resetDateObj = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const resetDate = format(resetDateObj, "MMM d, yyyy");

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-surface rounded-lg shadow-box p-6">
            <div className="h-5 w-32 bg-surface-2 rounded mb-3" />
            <div className="h-4 w-48 bg-surface-2 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-text-base">Organisation</h2>
        <p className="mt-1 text-sm text-text-muted">
          Plan details, usage, and organisation settings
        </p>
      </div>

      {/* Trial / Suspended banners */}
      {summary?.orgStatus === "TRIAL" && (
        <div className="bg-warn-bg border border-warn rounded-lg p-4 mb-6">
          <p className="text-sm text-warn">
            Your organisation is on a trial plan. Contact us to activate your
            full subscription.
          </p>
        </div>
      )}
      {summary?.orgStatus === "SUSPENDED" && (
        <div className="bg-err-bg border border-err rounded-lg p-4 mb-6">
          <p className="text-sm text-err">
            Your organisation is suspended. Please contact support.
          </p>
        </div>
      )}

      <div className="space-y-6">
        {/* Plan overview */}
        {summary && (
          <div className="bg-surface rounded-lg shadow-box p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-text-base">
                  {summary.plan.displayName} Plan
                </h3>
                <p className="text-sm text-text-faint mt-1">
                  {summary.plan.monthlyPriceCents > 0
                    ? `$${(summary.plan.monthlyPriceCents / 100).toFixed(2)}/month`
                    : "Free"}
                </p>
              </div>
              <StatusBadge status={summary.orgStatus} />
            </div>

            {/* Features */}
            {summary.plan.features &&
              Object.keys(summary.plan.features).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {Object.entries(summary.plan.features)
                    .filter(([, value]) => value)
                    .map(([key, value]) => (
                      <span
                        key={key}
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-tint text-accent"
                      >
                        {key
                          .replace(/([A-Z])/g, " $1")
                          .trim()
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                        {typeof value !== "boolean" && `: ${value}`}
                      </span>
                    ))}
                </div>
              )}

            {summary.plan.name.toLowerCase() !== "enterprise" && (
              <div className="mt-4">
                <a
                  href="mailto:support@refineai.com.au"
                  className="text-sm text-accent hover:underline"
                >
                  Contact us to upgrade
                </a>
              </div>
            )}
          </div>
        )}

        {/* Usage meters */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <UsageMeter
              label="Seats"
              used={summary.seats.used}
              max={summary.seats.max}
              remaining={summary.seats.remaining}
              percentUsed={summary.seats.percentUsed}
              link="/users"
              linkLabel="Manage users &rarr;"
            />
            <UsageMeter
              label="Documents"
              used={summary.documents.current}
              max={summary.documents.max}
              remaining={summary.documents.remaining}
              percentUsed={summary.documents.percentUsed}
              link="/documents"
              linkLabel="View documents &rarr;"
            />
            <UsageMeter
              label="API Calls This Month"
              used={summary.apiCalls.usedThisMonth}
              max={summary.apiCalls.max}
              remaining={summary.apiCalls.remaining}
              percentUsed={summary.apiCalls.percentUsed}
              subtitle={`Resets ${resetDate}`}
            />
          </div>
        )}

        {/* Organisation details */}
        {org && (
          <div className="bg-surface rounded-lg shadow-box p-6">
            <h3 className="text-sm font-semibold text-text-base mb-4">
              Organisation Details
            </h3>
            <dl className="space-y-3">
              <div className="flex items-center justify-between">
                <dt className="text-sm text-text-muted">Name</dt>
                <dd className="text-sm text-text-base">
                  {editingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        className="px-2 py-1 border border-subtle rounded bg-surface text-sm text-text-base"
                      />
                      <button
                        onClick={handleSaveName}
                        className="text-xs text-accent hover:underline"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingName(false)}
                        className="text-xs text-text-faint hover:text-text-muted"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <span>
                      {org.name}
                      {isAdmin && (
                        <button
                          onClick={handleStartEdit}
                          className="ml-2 text-xs text-accent hover:underline"
                        >
                          Edit
                        </button>
                      )}
                    </span>
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm text-text-muted">Slug</dt>
                <dd className="text-sm font-mono text-text-base">{org.slug}</dd>
              </div>
              {summary && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-text-muted">Plan</dt>
                  <dd className="text-sm text-text-base">
                    {summary.plan.displayName}
                  </dd>
                </div>
              )}
              <div className="flex items-center justify-between">
                <dt className="text-sm text-text-muted">Member since</dt>
                <dd className="text-sm text-text-base">
                  {format(new Date(org.createdAt), "MMM d, yyyy")}
                </dd>
              </div>
              {summary && (
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-text-muted">Storage used</dt>
                  <dd className="text-sm text-text-base">
                    {summary.storage.currentMB} MB
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>
    </div>
  );
}
